import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ProcessingJob } from "@/types/survey";
import { Brain, Check, Search, Settings, Hammer, X, FileText, Loader2 } from "lucide-react";
import { getQueryFn } from "@/lib/api";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";

interface ProcessingStatusProps {
  jobId: number;
  onComplete: (jobId: number) => void;
  onCancel: () => void;
}

interface StepProps {
  step: number;
  label: string;
  icon: LucideIcon;
  status: "completed" | "processing" | "pending";
}

// Step component with animations
function ProcessingStep({ label, icon: Icon, status }: StepProps) {
  return (
    <div className="relative">
      <motion.div 
        initial={false}
        animate={status === "processing" ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 1, repeat: status === "processing" ? Infinity : 0 }}
        className={`
          mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2
          ${status === "completed" ? "bg-primary text-primary-foreground" : ""}
          ${status === "processing" ? "bg-accent text-accent-foreground" : ""}
          ${status === "pending" ? "bg-muted text-muted-foreground" : ""}
        `}
      >
        {status === "completed" ? (
          <Check className="h-5 w-5" />
        ) : (
          <Icon className="h-5 w-5" />
        )}
      </motion.div>
      <p className={`text-sm font-medium ${status === "pending" ? "text-muted-foreground" : "text-foreground"}`}>
        {label}
      </p>
    </div>
  );
}

// Step definitions
const steps = [
  { icon: FileText, label: "Document Parsed" },
  { icon: Search, label: "Extracting Questions" },
  { icon: Settings, label: "Classifying Types" },
  { icon: Hammer, label: "Building Survey" },
];

export function ProcessingStatus({ jobId, onComplete, onCancel }: ProcessingStatusProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: job, error } = useQuery<ProcessingJob | null>({
    queryKey: [`/api/processing/${jobId}`],
    queryFn: getQueryFn<ProcessingJob | null>({ on401: "returnNull" }),
    refetchInterval: (query) => {
      const job = query?.state?.data as ProcessingJob | null;
      return job?.status === "completed" || job?.status === "failed" ? false : 2000;
    },
    retry: false,
  });

  useEffect(() => {
    if (job?.status === "completed") {
      toast({
        title: "Processing Complete",
        description: "Your survey has been successfully processed!",
      });
      // Invalidate both processing and surveys to update Recent Projects
      queryClient.invalidateQueries({ queryKey: ["/api/processing"] });
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
      onComplete(jobId);
    } else if (job?.status === "failed") {
      toast({
        title: "Processing Failed",
        description: job.error || "An error occurred while processing your document.",
        variant: "destructive",
      });
    }
  }, [job, jobId, onComplete, toast, queryClient]);

  const getStepStatus = (step: number): "completed" | "processing" | "pending" => {
    if (!job) return "pending";
    if (job.progress >= step * 25) {
      return "completed";
    } else if (job.progress >= (step - 1) * 25) {
      return "processing";
    }
    return "pending";
  };

  // Error state
  if (error) {
    return (
      <Card className="shadow-xl border-0 overflow-hidden mb-8">
        <div className="h-1 bg-gradient-to-r from-destructive via-destructive/50 to-destructive" />
        <CardContent className="p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <X className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">Processing Error</h3>
          <p className="text-muted-foreground mb-4">
            Failed to check processing status. Please try again.
          </p>
          <Button onClick={onCancel} variant="outline">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (!job) {
    return (
      <Card className="shadow-xl border-0 overflow-hidden mb-8">
        <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />
        <CardContent className="p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading processing status...</p>
        </CardContent>
      </Card>
    );
  }

  // Failed state
  if (job.status === "failed") {
    return (
      <Card className="shadow-xl border-0 overflow-hidden mb-8">
        <div className="h-1 bg-gradient-to-r from-destructive via-destructive/50 to-destructive" />
        <CardContent className="p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <X className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">Processing Failed</h3>
          {job.error && (
            <p className="text-muted-foreground mb-4">{job.error}</p>
          )}
          <Badge variant="secondary" className="mb-6">
            {job.fileName}
          </Badge>
          <div>
            <Button onClick={onCancel} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Processing state (main view)
  return (
    <Card className="shadow-xl border-0 overflow-hidden mb-8">
      {/* Gradient header bar */}
      <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />
      
      <CardContent className="p-8">
        <div className="text-center">
          {/* Animated AI icon */}
          <motion.div 
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-6"
          >
            <Brain className="h-10 w-10 text-primary" />
          </motion.div>
          
          <h3 className="text-2xl font-bold text-foreground mb-2">
            AI Processing Your Survey
          </h3>
          <p className="text-muted-foreground mb-2">
            Our AI is analyzing your document and extracting survey questions
          </p>
          <Badge variant="secondary" className="mb-8">
            {job.fileName}
          </Badge>

          {/* Processing Steps - 4 columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {steps.map((step, index) => (
              <ProcessingStep
                key={index}
                step={index + 1}
                label={step.label}
                icon={step.icon}
                status={getStepStatus(index + 1)}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">{job.progress}%</span>
            </div>
            <Progress value={job.progress} className="h-3" />
            
            {job.status === "processing" && (
              <p className="text-sm text-muted-foreground mt-2">
                Estimated time: {Math.max(1, Math.ceil((100 - job.progress) / 20))} min
              </p>
            )}
          </div>

          {/* Cancel button */}
          <Button variant="outline" size="sm" onClick={onCancel} className="mt-6">
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
